import {useState, useCallback, useMemo, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {COUNTRY_LANGUAGES_CODES} from '@/utils/constants/globalConstants';
import {selectCountryCode} from '@/store/slices/countryCodeSlice';
import {
  setActivityDisplayData,
  resetActivityData
} from '@/store/slices/activitySlice';
import {
  isFreeKickActivityType,
  isWheelOfFortuneActivityType,
  isBackgammonActivityType
} from '@/types/Activity';
import {getFileVerification} from '@/services/fileUploadService';
import {getSasToken} from '@/services/sasToken';
import {
  Constants,
  maxImageSizeInBytes,
  maxVideoSizeInBytes
} from '@/utils/constants/commonConstants';
import {FeatureConfig} from '@/utils/constants/featuresFlag';
import {
  generateVideoThumbnail,
  prepareVideoThumbnailFile
} from '@/helpers/shadcn/utils';
import {
  buildWebpImageUploadFormData,
  extractCdnUrlFromUploadResponse
} from '@/helpers/imageUploadForm';
import {sanitizeBlobPath} from '@/utils/commons';
import {useAzureBlob} from '@/hooks/useAzureBlob-hook';
import {useAppSelector} from '@/store';
import {
  isVideoDisplayOrderInvalid,
  normalizeDisplayOrderForCounter,
  parseValidDisplayOrder,
  sanitizeDisplayOrderInput
} from '@/utils/displayOrderValidation';

const EMPTY_LANG_DATA = {
  name: '',
  tagDescription: '',
  description: '',
  imageFile: null,
  videoFile: null,
  videoUri: '',
  videoThumbnailUrl: '',
  videoImageUrl: '',
  videoImageName: '',
  pointsUnitOfMeasure: '',
  noPointsAwarded: '',
  gameImageUri: ''
};

const MAX_IMAGE_SIZE_IN_KB = Math.floor(maxImageSizeInBytes / 1024);
const MAX_VIDEO_UPLOAD_RETRIES = 2;
const MAX_THUMBNAIL_GENERATION_RETRIES = 2;
const imageErrorDefault =
  'Invalid file format. Only .webp, .jpg, .png files are allowed.';

const normalizeDisplayOrder = (displayOrder) =>
  normalizeDisplayOrderForCounter(displayOrder, isVideoDisplayOrderInvalid);

const isEmptyValue = (v) => v === '' || v == null;

const isHttpUrl = (value) =>
  typeof value === 'string' && /^https?:\/\//i.test(value);

const isDataUrl = (value) =>
  typeof value === 'string' && value.startsWith('data:');

const areSliceArraysEqual = (prev, next) =>
  prev.length === next.length &&
  prev.every((value, index) => value === next[index]);

const normalizeSliceArray = (prev, length, value) => {
  const normalized = Array.from({length}, () => value);
  return areSliceArraysEqual(prev, normalized) ? prev : normalized;
};

const padOrTruncateSliceArray = (prev, length) => {
  if (prev.length === length) return prev;
  const updated = [...prev];
  while (updated.length < length) updated.push('');
  return updated.slice(0, length);
};

const FORMAT_ERROR_KEYWORDS = ['format', 'type', 'extension', 'accept'];

const resolveMediaFormatError = ({
  errorMessage,
  defaultFormatError,
  normalizeError,
  extraKeywords = [],
  passthrough
}) => {
  if (!errorMessage) return defaultFormatError;

  const normalizedError = normalizeError(errorMessage);
  const keywords = [...FORMAT_ERROR_KEYWORDS, ...extraKeywords];
  if (keywords.some((keyword) => normalizedError.includes(keyword))) {
    return defaultFormatError;
  }

  return passthrough(errorMessage, defaultFormatError);
};

const checkWofFormValid = ({
  allLangsValid,
  isSlicesValid,
  equalProbability,
  totalProbability,
  sliceProbabilities,
  slicePoints,
  equalPoints,
  slices
}) => {
  const activeProbabilities = sliceProbabilities.slice(0, slices);
  const activePoints = slicePoints.slice(0, slices);

  return (
    allLangsValid &&
    isSlicesValid &&
    (equalProbability || totalProbability === 100) &&
    (equalProbability || activeProbabilities.length === slices) &&
    (equalProbability || !activeProbabilities.some(isEmptyValue)) &&
    (!equalPoints || !isEmptyValue(slicePoints[0])) &&
    (equalPoints || activePoints.length === slices) &&
    (equalPoints || !activePoints.some(isEmptyValue))
  );
};

const useActivityDisplay = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const countryCode = useAppSelector(selectCountryCode);
  const formData = useAppSelector((state) => state.activity.formData);
  const {uploadFileOnBlob} = useAzureBlob();

  const languages = useMemo(
    () =>
      COUNTRY_LANGUAGES_CODES[countryCode] || [
        {code: 'en', title: 'English', languageId: 1}
      ],
    [countryCode]
  );

  const existingTranslations = formData?.translations;
  const existingMedia = formData?.mediaUrls;
  // For Wheel of Fortune the server uses `translations` + `displayConfig.asset`
  const existingWofTranslations = formData?.translations;
  const existingDisplayConfig = formData?.displayConfig;

  // Wheel of Fortune specific flag (must be declared before buildInitialLangData)
  const isWheelOfFortuneFlow = isWheelOfFortuneActivityType(
    formData?.activityType
  );

  // FreeKick flag – sync effect must skip when active (handled by useFreeKickGameDisplay)
  const isFreeKickFlow =
    isFreeKickActivityType(formData?.activityType) ||
    isBackgammonActivityType(formData?.activityType);

  const buildInitialLangData = useCallback(() => {
    const data = {};
    languages.forEach((lang) => {
      if (isWheelOfFortuneFlow) {
        // WOF reads from the new `translations` key, falling back to old `translations`
        const trans =
          existingWofTranslations?.[lang.code] ??
          existingTranslations?.[lang.code];
        const gameImage =
          existingDisplayConfig?.asset?.gameImage ??
          existingMedia?.find((m) => m.language === lang.code)?.gameImageUrl ??
          '';
        if (trans) {
          data[lang.code] = {
            ...EMPTY_LANG_DATA,
            name: trans?.title || trans?.name || '',
            tagDescription: trans?.tagDescription || '',
            description: trans?.gameDescription || '',
            gameImageUri: gameImage,
            pointsUnitOfMeasure: trans?.pointsUnitOfMeasure || '',
            noPointsAwarded: trans?.noPointsAwarded || ''
          };
        } else {
          data[lang.code] = {
            ...EMPTY_LANG_DATA,
            gameImageUri: gameImage
          };
        }
        return;
      }

      const translation = existingTranslations?.[lang.code];
      const media = Array.isArray(existingMedia)
        ? existingMedia.find((m) => m.language === lang.code)
        : undefined;

      if (translation || media) {
        data[lang.code] = {
          ...EMPTY_LANG_DATA,
          name: translation?.name || '',
          tagDescription: translation?.tagDescription || '',
          description: translation?.description || '',
          videoUri: media?.videoUrl || '',
          videoThumbnailUrl: media?.videoThumbnailUrl || '',
          videoImageName: media?.videoImageName || '',
          videoImageUrl: media?.videoImageUrl || '',
          gameImageUri: media?.gameImageUrl || media?.videoImageUrl || '',
          pointsUnitOfMeasure: translation?.pointsUnitOfMeasure || '',
          noPointsAwarded: translation?.noPointsAwarded || ''
        };
      } else {
        data[lang.code] = {...EMPTY_LANG_DATA};
      }
    });
    return data;
  }, [
    languages,
    existingTranslations,
    existingMedia,
    existingWofTranslations,
    existingDisplayConfig,
    isWheelOfFortuneFlow
  ]);

  const [langData, setLangData] = useState(buildInitialLangData);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [descActiveTabIndex, setDescActiveTabIndex] = useState(0);
  const [displayOrder, setDisplayOrderState] = useState(() =>
    normalizeDisplayOrder(formData?.displayOrder)
  );
  const displayOrderRef = useRef(displayOrder);
  displayOrderRef.current = displayOrder;

  const setDisplayOrder = useCallback((value) => {
    setDisplayOrderState(sanitizeDisplayOrderInput(value));
  }, []);

  useEffect(() => {
    if (isVideoDisplayOrderInvalid(displayOrderRef.current)) {
      return;
    }
    const nextValue = normalizeDisplayOrder(formData?.displayOrder);
    setDisplayOrderState((current) =>
      current === nextValue ? current : nextValue
    );
  }, [formData?.displayOrder]);

  const [isCancel, setIsCancel] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [videoUploadProgress, setVideoUploadProgress] = useState(0);
  const [imageValidationError, setImageValidationError] = useState('');
  const [videoValidationError, setVideoValidationError] = useState('');
  const latestVideoUploadRequestRef = useRef(0);

  // Wheel of Fortune specific state
  const existingWofData = formData?.wheelOfFortuneDisplayData;
  const existingGameConfig = formData?.displayConfig?.gameConfig;
  const existingWheelSlices = Array.isArray(existingGameConfig?.wheelSlices)
    ? existingGameConfig.wheelSlices
    : null;
  const existingWofSlices =
    existingWheelSlices?.length ?? existingWofData?.slices ?? 5;
  const hasExistingWofData =
    Array.isArray(existingGameConfig?.wheelSlices) || existingWofData != null;
  const existingWofEqualProbability =
    existingGameConfig?.equalProbabilityPerSlice ??
    existingWofData?.equalProbability ??
    !hasExistingWofData;
  const existingWofEqualPoints =
    existingGameConfig?.equalPointsPerSlice ??
    existingWofData?.equalPoints ??
    !hasExistingWofData;
  const existingWofSliceProbabilities =
    existingWheelSlices?.map((s) => String(s.probability ?? '')) ??
    existingWofData?.sliceProbabilities ??
    [];
  const existingWofSlicePoints =
    existingWheelSlices?.map((s) => String(s.rewardPoints ?? '')) ??
    existingWofData?.slicePoints ??
    [];
  const [slices, setSlices] = useState(existingWofSlices);
  const [equalProbability, setEqualProbability] = useState(
    existingWofEqualProbability
  );
  const [equalPoints, setEqualPoints] = useState(existingWofEqualPoints);
  const [sliceProbabilities, setSliceProbabilities] = useState(
    existingWofSliceProbabilities
  );
  const [slicePoints, setSlicePoints] = useState(existingWofSlicePoints);
  const [gameSettingsActiveTabIndex, setGameSettingsActiveTabIndex] =
    useState(0);

  const validSliceValues = [4, 5, 6, 7];
  const isSlicesValid = validSliceValues.includes(slices);

  // Normalize probabilities when equal mode is on or slice count changes
  useEffect(() => {
    if (equalProbability && isSlicesValid) {
      const equalValue = String(Number((100 / slices).toFixed(1)));
      setSliceProbabilities((prev) =>
        normalizeSliceArray(prev, slices, equalValue)
      );
    }
  }, [equalProbability, slices, isSlicesValid]);

  // Normalize points when equal mode is on or slice count changes
  useEffect(() => {
    if (equalPoints && isSlicesValid) {
      setSlicePoints((prev) =>
        normalizeSliceArray(prev, slices, prev[0] || '')
      );
    }
  }, [equalPoints, slices, isSlicesValid]);

  // Pad/truncate probabilities when equal mode is off and slice count changes
  useEffect(() => {
    if (!equalProbability && isSlicesValid) {
      setSliceProbabilities((prev) => padOrTruncateSliceArray(prev, slices));
    }
  }, [equalProbability, slices, isSlicesValid]);

  // Pad/truncate points when equal mode is off and slice count changes
  useEffect(() => {
    if (!equalPoints && isSlicesValid) {
      setSlicePoints((prev) => padOrTruncateSliceArray(prev, slices));
    }
  }, [equalPoints, slices, isSlicesValid]);

  const activeLangCode = languages[activeTabIndex]?.code;
  const descActiveLangCode = languages[descActiveTabIndex]?.code;
  const gameSettingsActiveLangCode =
    languages[gameSettingsActiveTabIndex]?.code;

  const currentLangData = langData[activeLangCode] || {...EMPTY_LANG_DATA};
  const descCurrentLangData = langData[descActiveLangCode] || {
    ...EMPTY_LANG_DATA
  };
  const gameSettingsCurrentLangData = langData[gameSettingsActiveLangCode] || {
    ...EMPTY_LANG_DATA
  };

  const updateLangField = useCallback((langCode, field, value) => {
    setLangData((prev) => {
      if (prev[langCode]?.[field] === value) return prev;
      return {
        ...prev,
        [langCode]: {
          ...prev[langCode],
          [field]: value
        }
      };
    });
  }, []);

  const updateField = useCallback(
    (field, value) => updateLangField(activeLangCode, field, value),
    [activeLangCode, updateLangField]
  );

  const updateDescField = useCallback(
    (field, value) => updateLangField(descActiveLangCode, field, value),
    [descActiveLangCode, updateLangField]
  );

  const updateGameSettingsField = useCallback(
    (field, value) => updateLangField(gameSettingsActiveLangCode, field, value),
    [gameSettingsActiveLangCode, updateLangField]
  );

  // Update a field across ALL languages (used for image/video which are shared)
  const updateFieldAllLanguages = useCallback(
    (field, value) => {
      setLangData((prev) => {
        const allSame = languages.every(
          (lang) => prev[lang.code]?.[field] === value
        );
        if (allSame) return prev;
        const updated = {...prev};
        languages.forEach((lang) => {
          updated[lang.code] = {
            ...updated[lang.code],
            [field]: value
          };
        });
        return updated;
      });
    },
    [languages]
  );

  const updateFieldsAllLanguages = useCallback(
    (fields) => {
      setLangData((prev) => {
        const entries = Object.entries(fields);
        const allSame = entries.every(([field, value]) =>
          languages.every((lang) => prev[lang.code]?.[field] === value)
        );
        if (allSame) return prev;

        const updated = {...prev};
        languages.forEach((lang) => {
          updated[lang.code] = {
            ...updated[lang.code],
            ...fields
          };
        });
        return updated;
      });
    },
    [languages]
  );

  const clearImageMediaFields = useCallback(() => {
    updateFieldsAllLanguages({
      imageFile: null,
      videoImageUrl: '',
      gameImageUri: ''
    });
  }, [updateFieldsAllLanguages]);

  const clearVideoMediaFields = useCallback(() => {
    updateFieldsAllLanguages({
      videoFile: null,
      videoUri: '',
      videoThumbnailUrl: ''
    });
  }, [updateFieldsAllLanguages]);

  const getVideoImageUrlFallback = useCallback(
    () => langData[activeLangCode]?.videoImageUrl || '',
    [activeLangCode, langData]
  );

  const isStaleVideoUpload = useCallback(
    (requestId) => latestVideoUploadRequestRef.current !== requestId,
    []
  );

  const onUploadProgress = (progressEvent) => {
    const progress = Math.round(
      (progressEvent.loaded * 100) / progressEvent.total
    );
    setImageUploadProgress(progress == 100 ? 99 : progress);
  };

  const uploadActivityImageToCdn = useCallback(async (file, onProgress) => {
    const {formData} = await buildWebpImageUploadFormData(file);
    const response = await getFileVerification(
      formData,
      Constants.ActivityMedia,
      onProgress,
      'webp'
    );

    return extractCdnUrlFromUploadResponse(response);
  }, []);

  const getFileFromUploadInput = useCallback((fileOrFiles) => {
    if (!fileOrFiles) return null;
    if (fileOrFiles instanceof Blob) return fileOrFiles;
    if (Array.isArray(fileOrFiles)) return fileOrFiles[0] || null;
    return fileOrFiles?.[0] || null;
  }, []);

  const validateImageFile = useCallback(async (file) => {
    // Validate file format (.webp, .jpg, .png) - only if file has a name
    if (file.name) {
      const normalizedName = file.name.toLowerCase();
      const isValidImageFormat =
        normalizedName.endsWith('.webp') ||
        normalizedName.endsWith('.jpg') ||
        normalizedName.endsWith('.png');

      if (!isValidImageFormat) {
        throw new Error(imageErrorDefault);
      }
    }

    // Validate file size
    if (file.size > maxImageSizeInBytes) {
      throw new Error(
        `Image size exceeds ${MAX_IMAGE_SIZE_IN_KB} KB. Please upload a smaller image.`
      );
    }
  }, []);

  const validateVideoFile = useCallback(async (file) => {
    // Validate file format (.mp4) - only if file has a name
    if (file.name && !file.name.toLowerCase().endsWith('.mp4')) {
      throw new Error('Invalid file format. Only .MP4 files are allowed.');
    }

    // Validate file size (max 100 MB = 104857600 bytes)
    if (file.size > maxVideoSizeInBytes) {
      throw new Error(
        'Video size exceeds 100 MB. Please upload a smaller video.'
      );
    }
  }, []);

  const generateBestEffortVideoThumbnail = useCallback(
    async (cdnUrl) => {
      if (cdnUrl) {
        for (
          let attempt = 1;
          attempt <= MAX_THUMBNAIL_GENERATION_RETRIES;
          attempt += 1
        ) {
          const thumbnailFromCdn = await generateVideoThumbnail(cdnUrl);
          if (thumbnailFromCdn) return thumbnailFromCdn;
        }
      }

      // Keep preview available when thumbnail extraction fails.
      return getVideoImageUrlFallback();
    },
    [getVideoImageUrlFallback]
  );

  const resolveVideoThumbnailToCdnUrl = useCallback(
    async (thumbnail) => {
      if (!thumbnail) return '';
      if (isHttpUrl(thumbnail)) return thumbnail;
      if (!isDataUrl(thumbnail)) return '';

      try {
        const thumbnailFile = await prepareVideoThumbnailFile(thumbnail, {
          maxBytes: maxImageSizeInBytes
        });
        if (!thumbnailFile) {
          return getVideoImageUrlFallback();
        }
        const cdnUrl = await uploadActivityImageToCdn(thumbnailFile);
        return cdnUrl || getVideoImageUrlFallback();
      } catch {
        // Soft-fail: keep video upload success; fall back to display image URL.
        return getVideoImageUrlFallback();
      }
    },
    [getVideoImageUrlFallback, uploadActivityImageToCdn]
  );

  const handleImageUpload = useCallback(
    async (fileOrFiles) => {
      const file = getFileFromUploadInput(fileOrFiles);
      if (!file) return;

      // Clear previous errors
      setImageValidationError('');

      try {
        await validateImageFile(file);
      } catch (error) {
        setImageValidationError(
          error?.message || 'Failed to validate image. Please try again.'
        );
        return;
      }

      try {
        setImageUploadProgress(0);
        const cdnUrl = await uploadActivityImageToCdn(file, onUploadProgress);

        setImageUploadProgress(100);

        updateFieldsAllLanguages({
          imageFile: file,
          videoImageUrl: cdnUrl,
          gameImageUri: cdnUrl
        });
        setImageValidationError('');
      } catch (error) {
        // Upload failed — clear any partial state
        setImageUploadProgress(0);
        clearImageMediaFields();

        // Show error message
        setImageValidationError(
          error?.message || 'Failed to upload image. Please try again.'
        );
      }
    },
    [
      getFileFromUploadInput,
      updateFieldsAllLanguages,
      clearImageMediaFields,
      uploadActivityImageToCdn,
      validateImageFile
    ]
  );

  const handleImageDelete = useCallback(() => {
    clearImageMediaFields();
    setImageValidationError('');
  }, [clearImageMediaFields]);

  const handleVideoUpload = useCallback(
    async (fileOrFiles) => {
      const file = getFileFromUploadInput(fileOrFiles);
      if (!file) return;

      const requestId = latestVideoUploadRequestRef.current + 1;
      latestVideoUploadRequestRef.current = requestId;

      // Clear previous errors
      setVideoValidationError('');

      try {
        await validateVideoFile(file);
      } catch (error) {
        setVideoValidationError(
          error?.message || 'Failed to validate video. Please try again.'
        );
        return;
      }

      try {
        setVideoUploadProgress(0);
        // Clear stale video data while a new upload is in progress.
        clearVideoMediaFields();

        let sasResponse;
        let uploadResult;
        let lastUploadError;
        for (
          let attempt = 1;
          attempt <= MAX_VIDEO_UPLOAD_RETRIES;
          attempt += 1
        ) {
          try {
            // Step 1: Get pre-signed upload URL
            sasResponse = await getSasToken(
              file.name,
              Constants.ActivityMedia,
              {mediaType: 'video'}
            );

            if (!sasResponse?.sasToken) {
              throw new Error('Failed to get upload URL');
            }

            // Step 2: Upload file to Azure Blob Storage with progress
            uploadResult = await uploadFileOnBlob(
              sasResponse,
              file,
              (progress) => {
                if (isStaleVideoUpload(requestId)) return;
                setVideoUploadProgress(progress == 100 ? 99 : progress);
              }
            );

            if (!uploadResult || uploadResult.message !== 'Success') {
              throw new Error('Video upload failed');
            }

            break;
          } catch (error) {
            lastUploadError = error;
            if (attempt === MAX_VIDEO_UPLOAD_RETRIES) {
              throw lastUploadError;
            }
          }
        }

        if (isStaleVideoUpload(requestId)) {
          return;
        }

        setVideoUploadProgress(100);

        const VITE_BLOB_URL =
          import.meta.env.VITE_BLOB_URL ||
          import.meta.env[FeatureConfig.blobURL];

        const safeDirectory = sanitizeBlobPath(sasResponse?.directory);
        const safeFileName = sanitizeBlobPath(sasResponse?.fileName);

        const videoUri =
          VITE_BLOB_URL && safeDirectory && safeFileName
            ? `${VITE_BLOB_URL}${safeDirectory}${safeFileName}`
            : '';

        const thumbnailSourceUrl =
          videoUri && sasResponse?.sasToken
            ? `${videoUri}?${sasResponse.sasToken}`
            : '';

        const thumbnail =
          await generateBestEffortVideoThumbnail(thumbnailSourceUrl);

        /* c8 ignore next 3 */
        if (isStaleVideoUpload(requestId)) {
          return;
        }

        const thumbnailUrl = await resolveVideoThumbnailToCdnUrl(thumbnail);

        /* c8 ignore next 3 */
        if (isStaleVideoUpload(requestId)) {
          return;
        }

        updateFieldsAllLanguages({
          videoFile: file,
          videoUri,
          videoThumbnailUrl: thumbnailUrl
        });
        setVideoValidationError('');
      } catch (error) {
        /* c8 ignore next 3 */
        if (isStaleVideoUpload(requestId)) {
          return;
        }

        // Upload failed — clear any partial state
        setVideoUploadProgress(0);
        clearVideoMediaFields();

        // Show error message
        setVideoValidationError(
          error?.message || 'Failed to upload video. Please try again.'
        );
      }
    },
    [
      getFileFromUploadInput,
      clearVideoMediaFields,
      updateFieldsAllLanguages,
      uploadFileOnBlob,
      validateVideoFile,
      generateBestEffortVideoThumbnail,
      resolveVideoThumbnailToCdnUrl,
      isStaleVideoUpload
    ]
  );

  const handleVideoDelete = useCallback(() => {
    clearVideoMediaFields();
    setVideoValidationError('');
  }, [clearVideoMediaFields]);

  const handleVideoFileError = useCallback((errorMessage) => {
    const defaultFormatError =
      'Invalid file format. Only .MP4 files are allowed.';

    setVideoValidationError(
      resolveMediaFormatError({
        errorMessage,
        defaultFormatError,
        normalizeError: (message) => String(message).toLowerCase(),
        passthrough: (message) => String(message)
      })
    );
  }, []);

  const handleImageFileError = useCallback((errorMessage) => {
    setImageValidationError(
      resolveMediaFormatError({
        errorMessage,
        defaultFormatError: imageErrorDefault,
        normalizeError: (message) => JSON.stringify(message).toLowerCase(),
        extraKeywords: ['file-invalid-type'],
        passthrough: (message, defaultFormatError) =>
          typeof message === 'string' ? message : defaultFormatError
      })
    );
  }, []);

  const isLangValid = useCallback(
    (langCode) => {
      const data = langData[langCode];
      if (!data) return false;

      if (isWheelOfFortuneFlow) {
        return (
          data.name.trim() !== '' &&
          data.tagDescription.trim() !== '' &&
          (data.imageFile !== null || data.gameImageUri !== '') &&
          data.pointsUnitOfMeasure.trim() !== '' &&
          data.noPointsAwarded.trim() !== ''
        );
      }

      const baseValid =
        data.name.trim() !== '' &&
        data.tagDescription.trim() !== '' &&
        (data.imageFile !== null || data.videoImageUrl !== '');

      return baseValid && (data.videoFile !== null || data.videoUri !== '');
    },
    [langData, isWheelOfFortuneFlow]
  );

  const totalProbability = useMemo(() => {
    if (equalProbability) return 100;
    const raw = sliceProbabilities
      .slice(0, slices)
      .reduce((sum, val) => sum + (Number.parseFloat(val) || 0), 0);
    return Math.round(raw);
  }, [equalProbability, sliceProbabilities, slices]);

  const isFormValid = useMemo(() => {
    const allLangsValid = languages.every((lang) => isLangValid(lang.code));

    return isWheelOfFortuneFlow
      ? checkWofFormValid({
          allLangsValid,
          isSlicesValid,
          equalProbability,
          totalProbability,
          sliceProbabilities,
          slicePoints,
          equalPoints,
          slices
        })
      : !isVideoDisplayOrderInvalid(displayOrder) && allLangsValid;
  }, [
    languages,
    isLangValid,
    displayOrder,
    isWheelOfFortuneFlow,
    equalProbability,
    totalProbability,
    isSlicesValid,
    sliceProbabilities,
    slicePoints,
    equalPoints,
    slices
  ]);

  // Sync display data to Redux formData in API format (like Setup's useEffect sync)
  useEffect(() => {
    // FreeKick translations are managed by useFreeKickGameDisplay – skip here
    if (isFreeKickFlow) return;

    if (isWheelOfFortuneFlow) {
      // Build translations for WOF (flat per-language object with camelCase keys)
      const translations = {};
      languages.forEach((lang) => {
        const data = langData[lang.code];
        if (data) {
          translations[lang.code] = {
            title: data.name || '',
            tagDescription: data.tagDescription || '',
            pointsUnitOfMeasure: data.pointsUnitOfMeasure || '',
            noPointsAwarded: data.noPointsAwarded || ''
          };
        }
      });

      // Build wheelSlices from local state
      const SLICE_COLORS_HEX = [
        '#4780BF', // Blue
        '#645191', // Purple
        '#E84A5F', // Pink
        '#D82A1C', // Red
        '#E98908', // Orange
        '#FBC356', // Yellow
        '#009339' // Green
      ];
      const wheelSlices = Array.from({length: slices}, (_, i) => {
        const points = Number(slicePoints[i]) || 0;
        const label = {};
        languages.forEach((lang) => {
          const data = langData[lang.code];
          if (points === 0) {
            label[lang.code] = data?.noPointsAwarded || '';
          } else {
            label[lang.code] =
              `${points} ${data?.pointsUnitOfMeasure || ''}`.trim();
          }
        });
        return {
          label,
          rewardPoints: points,
          probability: Number(sliceProbabilities[i]) || 0,
          color: SLICE_COLORS_HEX[i] || SLICE_COLORS_HEX[0]
        };
      });

      // Pick gameImage from first language that has it
      const firstLangWithImage = languages.find(
        (lang) => langData[lang.code]?.gameImageUri
      );
      const gameImageUri = firstLangWithImage
        ? langData[firstLangWithImage.code].gameImageUri
        : '';

      // Preserve any existing displayConfig fields (setup step sets budget, startTime, etc.)
      const existingDisplayConfig =
        formData?.displayConfig && typeof formData.displayConfig === 'object'
          ? formData.displayConfig
          : {};

      const displayConfig = {
        ...existingDisplayConfig,
        asset: {
          ...(existingDisplayConfig.asset || {}),
          gameImage: gameImageUri
        },
        gameConfig: {
          ...(existingDisplayConfig.gameConfig || {}),
          equalProbabilityPerSlice: equalProbability,
          equalPointsPerSlice: equalPoints,
          wheelSlices
        }
      };

      dispatch(
        setActivityDisplayData({
          displayConfig,
          translations
        })
      );
      return;
    }

    // Non-WOF flow (Video): keep existing behavior
    const translations = {};
    languages.forEach((lang) => {
      const data = langData[lang.code];
      if (data) {
        translations[lang?.code] = {
          name: data?.name || '',
          tagDescription: data?.tagDescription || '',
          description: data?.description || '',
          pointsUnitOfMeasure: data?.pointsUnitOfMeasure || '',
          noPointsAwarded: data?.noPointsAwarded || ''
        };
      }
    });

    const mediaUrls = languages.map((lang) => {
      const data = langData[lang?.code] || {};
      return {
        videoImageName: data.imageFile?.name || data?.videoImageName || '',
        language: lang?.code,
        videoImageUrl: data?.videoImageUrl || '',
        gameImageUrl: data?.gameImageUri || '',
        videoUrl: data?.videoUri || '',
        videoThumbnailUrl: data?.videoThumbnailUrl || ''
      };
    });

    const parsedDisplayOrder = parseValidDisplayOrder(
      displayOrder,
      isVideoDisplayOrderInvalid
    );

    const payload = {
      translations,
      mediaUrls,
      ...(parsedDisplayOrder !== null ? {displayOrder: parsedDisplayOrder} : {})
    };

    dispatch(setActivityDisplayData(payload));
    // formData reference is intentionally omitted to avoid loops; we snapshot
    // it via the closure on every dispatch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    langData,
    displayOrder,
    languages,
    dispatch,
    isFreeKickFlow,
    isWheelOfFortuneFlow,
    slices,
    equalProbability,
    equalPoints,
    sliceProbabilities,
    slicePoints
  ]);

  const handleNext = useCallback(() => {
    navigate(ROUTES.ACTIVITY_REVIEW);
  }, [navigate]);

  const handleCancel = () => setIsCancel(true);

  const onConfirmCancel = () => {
    dispatch(resetActivityData({activityType: '', hasEndDate: false}));
    navigate(ROUTES.ACTIVITIES);
  };

  const onDismissCancel = () => setIsCancel(false);

  return {
    languages,
    activeTabIndex,
    setActiveTabIndex,
    descActiveTabIndex,
    setDescActiveTabIndex,
    activeLangCode,
    descActiveLangCode,
    currentLangData,
    descCurrentLangData,
    updateField,
    updateDescField,
    updateFieldAllLanguages,
    displayOrder,
    setDisplayOrder,
    isFormValid,
    handleImageUpload,
    handleImageDelete,
    handleImageFileError,
    handleVideoUpload,
    handleVideoDelete,
    handleVideoFileError,
    imageUploadProgress,
    videoUploadProgress,
    handleNext,
    handleCancel,
    onConfirmCancel,
    onDismissCancel,
    isCancel,
    showGuidelines,
    setShowGuidelines,
    imageValidationError,
    videoValidationError,
    // Wheel of Fortune
    isWheelOfFortuneFlow,
    slices,
    setSlices,
    equalProbability,
    setEqualProbability,
    equalPoints,
    setEqualPoints,
    sliceProbabilities,
    setSliceProbabilities,
    slicePoints,
    setSlicePoints,
    gameSettingsActiveTabIndex,
    setGameSettingsActiveTabIndex,
    gameSettingsCurrentLangData,
    updateGameSettingsField,
    totalProbability
  };
};

export default useActivityDisplay;
